import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../stores/appStore';
import { useAvatars, useUpdateAvatar, useUpdateEmail, useUpdatePassword, useLeaderboard } from '../hooks/useGame';
import AnimatedTabs from './AnimatedTabs';
import type { AvatarInfo, LeaderboardEntry } from '../services/api';
import './ProfileModal.css';

interface ProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type MainTab = 'profile' | 'avatar' | 'leaderboard';

// Helper to normalize avatar data (handles both string and object formats)
function getAvatarPath(avatar: string | AvatarInfo): string {
    return typeof avatar === 'string' ? avatar : avatar.path;
}

function isAvatarLocked(avatar: string | AvatarInfo): boolean {
    return typeof avatar === 'object' && avatar.locked;
}

function getUnlockRequirement(avatar: string | AvatarInfo): string | undefined {
    return typeof avatar === 'object' ? avatar.unlock_requirement : undefined;
}

type GameMode = 'classic' | 'flip' | 'bridge';

const MODE_LABELS: Record<GameMode, string> = {
    classic: '🎯 Classic',
    flip: '🔄 Flip',
    bridge: '🌉 Bridge',
};

const MEDAL_EMOJIS = ['🥇', '🥈', '🥉'];

function PodiumPlace({ entry, place }: { entry?: LeaderboardEntry; place: 1 | 2 | 3 }) {
    const heights = { 1: 120, 2: 85, 3: 65 };
    const colors = {
        1: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
        2: 'linear-gradient(180deg, #C0C0C0 0%, #A0A0A0 100%)',
        3: 'linear-gradient(180deg, #CD7F32 0%, #8B4513 100%)',
    };

    if (!entry) {
        return (
            <div className={`leaderboard-podium-place leaderboard-podium-${place} leaderboard-podium-empty`}>
                <div className="leaderboard-podium-avatar-container">
                    <div className="leaderboard-podium-avatar empty">?</div>
                </div>
                <div
                    className="leaderboard-podium-stand"
                    style={{
                        height: heights[place],
                        background: 'linear-gradient(180deg, #3a3a5a 0%, #2a2a3a 100%)',
                    }}
                >
                    <span className="leaderboard-podium-rank">{place}</span>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className={`leaderboard-podium-place leaderboard-podium-${place}`}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: place === 1 ? 0.2 : place === 2 ? 0.1 : 0.3 }}
        >
            <div className="leaderboard-podium-avatar-container">
                {entry.avatar ? (
                    <img
                        src={`/static/avatars/${entry.avatar}`}
                        alt={entry.username}
                        className="leaderboard-podium-avatar"
                    />
                ) : (
                    <div className="leaderboard-podium-avatar default">👤</div>
                )}
                <span className="leaderboard-podium-medal">{MEDAL_EMOJIS[place - 1]}</span>
            </div>

            <div className="leaderboard-podium-username">{entry.username}</div>
            <div className="leaderboard-podium-stats">
                <span className="leaderboard-podium-percentage">{entry.percentage}%</span>
                <span className="leaderboard-podium-coins">🪙 {entry.coins.toLocaleString()}</span>
            </div>

            <motion.div
                className="leaderboard-podium-stand"
                style={{
                    height: heights[place],
                    background: colors[place],
                }}
                initial={{ height: 0 }}
                animate={{ height: heights[place] }}
                transition={{ delay: 0.4, duration: 0.4, ease: 'easeOut' }}
            >
                <span className="leaderboard-podium-rank">{place}</span>
            </motion.div>
        </motion.div>
    );
}

function LeaderboardContent() {
    const [mode, setMode] = useState<GameMode>('classic');
    const { data, isLoading, error } = useLeaderboard();

    const modeTabs = [
        { id: 'classic' as GameMode, label: MODE_LABELS.classic },
        { id: 'flip' as GameMode, label: MODE_LABELS.flip },
        { id: 'bridge' as GameMode, label: MODE_LABELS.bridge },
    ];

    const entries = data?.leaderboards?.[mode] || [];
    const first = entries.find(e => e.rank === 1);
    const second = entries.find(e => e.rank === 2);
    const third = entries.find(e => e.rank === 3);

    return (
        <div className="leaderboard-content">
            <div className="leaderboard-mode-tabs">
                <AnimatedTabs
                    tabs={modeTabs}
                    activeTab={mode}
                    onTabChange={(id) => setMode(id as GameMode)}
                    layoutId="leaderboard-mode-tabs"
                />
            </div>

            <div className="leaderboard-podium-section">
                {isLoading ? (
                    <div className="leaderboard-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading leaderboard...</p>
                    </div>
                ) : error ? (
                    <div className="leaderboard-error">
                        <p>Failed to load leaderboard</p>
                    </div>
                ) : entries.length === 0 ? (
                    <div className="leaderboard-empty">
                        <p>🏆 No champions yet!</p>
                        <span>Be the first to complete puzzles</span>
                    </div>
                ) : (
                    <div className="leaderboard-podium-container">
                        <PodiumPlace entry={second} place={2} />
                        <PodiumPlace entry={first} place={1} />
                        <PodiumPlace entry={third} place={3} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
    const user = useAppStore(state => state.user);

    // Main tab state
    const [mainTab, setMainTab] = useState<MainTab>('profile');

    // Avatar tab state
    const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar);
    const [avatarCategory, setAvatarCategory] = useState<string>('general');
    const [lockedMessage, setLockedMessage] = useState<string | null>(null);

    // Profile tab state
    const [email, setEmail] = useState(user?.email || '');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Hooks
    const { data: avatars, isLoading: isLoadingAvatars } = useAvatars();
    const { mutate: updateAvatar, isPending: isUpdatingAvatar } = useUpdateAvatar();
    const { mutate: updateEmail, isPending: isUpdatingEmail } = useUpdateEmail();
    const { mutate: updatePassword, isPending: isUpdatingPassword } = useUpdatePassword();

    const isGuest = user?.is_guest;

    useEffect(() => {
        if (isOpen && user) {
            setSelectedAvatar(user.avatar);
            setEmail(user.email || '');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setLockedMessage(null);
            setProfileMessage(null);
        }
        if (avatars && avatars.length > 0 && !avatars.find((a: any) => a.id === avatarCategory)) {
            setAvatarCategory(avatars[0].id);
        }
    }, [isOpen, user, avatars]);

    // Clear messages when switching tabs
    useEffect(() => {
        setLockedMessage(null);
        setProfileMessage(null);
    }, [mainTab, avatarCategory]);

    if (!user) return null;

    const handleAvatarClick = (avatar: string | AvatarInfo) => {
        if (isAvatarLocked(avatar)) {
            const requirement = getUnlockRequirement(avatar);
            setLockedMessage(requirement || 'This avatar is locked');
        } else {
            setSelectedAvatar(getAvatarPath(avatar));
            setLockedMessage(null);
        }
    };

    const handleSaveAvatar = () => {
        if (selectedAvatar && selectedAvatar !== user.avatar) {
            updateAvatar(selectedAvatar, {
                onSuccess: () => {
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    const handleUpdateEmail = () => {
        if (!email.trim()) {
            setProfileMessage({ type: 'error', text: 'Email cannot be empty' });
            return;
        }

        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailPattern.test(email)) {
            setProfileMessage({ type: 'error', text: 'Please enter a valid email address' });
            return;
        }

        updateEmail(email, {
            onSuccess: (data) => {
                if (data.success) {
                    setProfileMessage({ type: 'success', text: 'Email updated successfully!' });
                } else {
                    setProfileMessage({ type: 'error', text: data.error || 'Failed to update email' });
                }
            },
            onError: (error) => {
                setProfileMessage({ type: 'error', text: error.message || 'Failed to update email' });
            }
        });
    };

    const handleUpdatePassword = () => {
        if (!currentPassword) {
            setProfileMessage({ type: 'error', text: 'Please enter your current password' });
            return;
        }
        if (!newPassword) {
            setProfileMessage({ type: 'error', text: 'Please enter a new password' });
            return;
        }
        if (newPassword.length < 6) {
            setProfileMessage({ type: 'error', text: 'New password must be at least 6 characters' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setProfileMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }

        updatePassword({ currentPassword, newPassword }, {
            onSuccess: (data) => {
                if (data.success) {
                    setProfileMessage({ type: 'success', text: 'Password updated successfully!' });
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                } else {
                    setProfileMessage({ type: 'error', text: data.error || 'Failed to update password' });
                }
            },
            onError: (error) => {
                setProfileMessage({ type: 'error', text: error.message || 'Failed to update password' });
            }
        });
    };

    const mainTabs = [
        { id: 'profile' as MainTab, label: '⚙️ Account' },
        { id: 'avatar' as MainTab, label: '🎨 Avatar' },
        { id: 'leaderboard' as MainTab, label: '🏆 Leaderboard' },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="profile-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="profile-modal"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="profile-modal-header">
                            <h2 className="profile-modal-title">Edit Profile</h2>
                            <button className="profile-modal-close" onClick={onClose}>×</button>
                        </div>

                        {/* Main Tab Selector */}
                        <div className="profile-main-tabs">
                            <AnimatedTabs
                                tabs={mainTabs}
                                activeTab={mainTab}
                                onTabChange={setMainTab}
                                layoutId="profile-main-tabs"
                            />
                        </div>

                        <div className="profile-section">
                            {mainTab === 'profile' && (
                                <div className="profile-account-section">
                                    {isGuest ? (
                                        <div className="guest-notice">
                                            <div className="guest-notice-icon">👤</div>
                                            <h3>Guest Account</h3>
                                            <p>You're playing as a guest. Create an account to save your progress, unlock achievements, and customize your profile!</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Email Section */}
                                            <div className="profile-form-section">
                                                <h3 className="profile-form-title">📧 Email Address</h3>
                                                <div className="profile-form-group">
                                                    <label htmlFor="email">Email</label>
                                                    <input
                                                        id="email"
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        placeholder="your@email.com"
                                                        className="profile-input"
                                                    />
                                                </div>
                                                <button
                                                    className="profile-update-btn"
                                                    onClick={handleUpdateEmail}
                                                    disabled={isUpdatingEmail || email === user.email}
                                                >
                                                    {isUpdatingEmail ? 'Updating...' : 'Update Email'}
                                                </button>
                                            </div>

                                            {/* Password Section */}
                                            <div className="profile-form-section">
                                                <h3 className="profile-form-title">🔐 Change Password</h3>
                                                <div className="profile-form-group">
                                                    <label htmlFor="current-password">Current Password</label>
                                                    <input
                                                        id="current-password"
                                                        type="password"
                                                        value={currentPassword}
                                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                                        placeholder="Enter current password"
                                                        className="profile-input"
                                                    />
                                                </div>
                                                <div className="profile-form-group">
                                                    <label htmlFor="new-password">New Password</label>
                                                    <input
                                                        id="new-password"
                                                        type="password"
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        placeholder="Enter new password (min 6 chars)"
                                                        className="profile-input"
                                                    />
                                                </div>
                                                <div className="profile-form-group">
                                                    <label htmlFor="confirm-password">Confirm New Password</label>
                                                    <input
                                                        id="confirm-password"
                                                        type="password"
                                                        value={confirmPassword}
                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                        placeholder="Confirm new password"
                                                        className="profile-input"
                                                    />
                                                </div>
                                                <button
                                                    className="profile-update-btn"
                                                    onClick={handleUpdatePassword}
                                                    disabled={isUpdatingPassword || !currentPassword || !newPassword}
                                                >
                                                    {isUpdatingPassword ? 'Updating...' : 'Change Password'}
                                                </button>
                                            </div>

                                            {/* Status Message */}
                                            {profileMessage && (
                                                <motion.div
                                                    className={`profile-message ${profileMessage.type}`}
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                >
                                                    {profileMessage.type === 'success' ? '✅' : '❌'} {profileMessage.text}
                                                </motion.div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {mainTab === 'avatar' && (
                                <>
                                    {isLoadingAvatars ? (
                                        <div className="loading-spinner">Loading avatars...</div>
                                    ) : (
                                        <>
                                            <AnimatedTabs
                                                tabs={avatars?.map((category: any) => ({ id: category.id, label: category.name })) || []}
                                                activeTab={avatarCategory}
                                                onTabChange={setAvatarCategory}
                                                layoutId="profile-avatar-tabs"
                                            />

                                            {lockedMessage && (
                                                <motion.div
                                                    className="locked-avatar-message"
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                >
                                                    🔒 {lockedMessage}
                                                </motion.div>
                                            )}

                                            <div className="avatar-grid-container">
                                                {avatars?.find((c: any) => c.id === avatarCategory)?.avatars.map((avatar: string | AvatarInfo) => {
                                                    const path = getAvatarPath(avatar);
                                                    const locked = isAvatarLocked(avatar);
                                                    return (
                                                        <button
                                                            key={path}
                                                            className={`avatar-option ${selectedAvatar === path ? 'selected' : ''} ${locked ? 'locked' : ''}`}
                                                            onClick={() => handleAvatarClick(avatar)}
                                                        >
                                                            <img src={`/static/avatars/${path}`} alt="Avatar option" />
                                                            {locked && <div className="avatar-lock-overlay">🔒</div>}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {mainTab === 'leaderboard' && (
                                <LeaderboardContent />
                            )}
                        </div>

                        <div className="profile-modal-footer">
                            {mainTab === 'avatar' && (
                                <button className="submit-btn" onClick={handleSaveAvatar} disabled={isUpdatingAvatar}>
                                    {isUpdatingAvatar ? 'Saving...' : 'Save Avatar'}
                                </button>
                            )}
                            {mainTab === 'profile' && !isGuest && (
                                <button className="submit-btn secondary" onClick={onClose}>
                                    Close
                                </button>
                            )}
                            {mainTab === 'profile' && isGuest && (
                                <button className="submit-btn" onClick={onClose}>
                                    Close
                                </button>
                            )}
                            {mainTab === 'leaderboard' && (
                                <p className="leaderboard-footer-note">By % of all puzzles completed</p>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
